import React from "react";
import { TrendingDown, AlertCircle, Zap } from "lucide-react";
import { Card } from "../ui/card";
import { motion } from "framer-motion";

export default function StatsSection() {
  const stats = [
    { value: "45%", label: "of high school seniors perform below basic level in math", delay: 0.1 },
    { value: "2.5x", label: "higher college dropout rate for students without proper support", delay: 0.2 },
    { value: "$50K", label: "average lifetime income loss per grade level behind", delay: 0.3 },
    { value: "73%", label: "of students improve with consistent one-on-one tutoring", delay: 0.4 },
  ];

  return (
    <div className="
      py-20 relative overflow-hidden
      bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50
      dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950
    ">
      {/* Background glow */}
      <div className="absolute inset-0 opacity-20 dark:opacity-20">
        <div className="absolute top-20 left-0 w-96 h-96 bg-blue-300 dark:bg-red-600/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-purple-300 dark:bg-orange-500/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="
              inline-flex items-center gap-2 px-6 py-3 rounded-full mb-6 shadow-lg border-2
              bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 border-blue-200
              dark:bg-gradient-to-r dark:from-red-900/60 dark:to-orange-900/60
              dark:text-red-200 dark:border-red-700/50 dark:backdrop-blur
            "
            whileHover={{ scale: 1.05 }}
          >
            <AlertCircle className="w-5 h-5" />
            <span className="font-bold text-lg">The Education Crisis</span>
          </motion.div>

          <h2 className="
            text-4xl md:text-6xl font-extrabold mb-6 pb-2
            bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900 bg-clip-text text-transparent
            dark:bg-gradient-to-r dark:from-white dark:via-red-400 dark:to-white
          ">
            Students Are Falling Behind
          </h2>

          <p className="
            text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed
            text-gray-600
            dark:text-zinc-400
          ">
            National data reveals a system where{" "}
            <span className="font-bold text-blue-600 dark:text-red-400">
              nearly half of all students struggle
            </span>{" "}
            without the individualized support they need.
          </p>
        </motion.div>

        {/* Stats grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: stat.delay, duration: 0.5 }}
            >
              <Card className="
                p-6 h-full relative overflow-hidden transition-all duration-300 group
                bg-gradient-to-br from-white to-blue-50/50 border-3 border-blue-200
                hover:border-blue-400 hover:shadow-2xl
                dark:bg-gradient-to-br dark:from-zinc-900 dark:to-zinc-800
                dark:border-red-800/40 dark:hover:border-red-500/70
                dark:hover:shadow-[0_0_40px_rgba(239,68,68,0.25)]
              ">
                <div className="
                  absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300
                  bg-gradient-to-br from-blue-500/0 to-blue-500/5
                  dark:from-red-500/0 dark:to-red-500/10
                " />

                <div className="relative flex flex-col items-center text-center gap-4 h-full">
                  <div className="
                    w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg
                    bg-gradient-to-br from-blue-500 to-purple-600
                    dark:from-red-500 dark:to-red-600
                    group-hover:scale-110 transition-transform
                  ">
                    <TrendingDown className="w-7 h-7 text-white" />
                  </div>

                  <div className="
                    text-5xl font-black bg-clip-text text-transparent
                    bg-gradient-to-br from-blue-600 to-purple-700
                    dark:from-red-400 dark:to-red-600
                  ">
                    {stat.value}
                  </div>

                  <p className="
                    leading-relaxed font-medium min-h-[3rem] flex items-center
                    text-gray-700
                    dark:text-zinc-300
                  ">
                    {stat.label}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Bottom callout */}
        <motion.div
          className="
            relative p-10 rounded-3xl overflow-hidden
            bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 shadow-2xl
            dark:from-red-600 dark:via-orange-600 dark:to-amber-500
            dark:shadow-[0_0_60px_rgba(249,115,22,0.35)]
          "
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />

          <div className="relative flex items-start gap-4">
            <div className="hidden md:flex w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl items-center justify-center">
              <Zap className="w-8 h-8 text-white" />
            </div>

            <p className="text-xl md:text-2xl text-white leading-relaxed font-medium">
              <span className="font-black drop-shadow-lg">
                These numbers tell a story bigger than test scores.
              </span>{" "}
              They reveal a system where traditional methods aren't closing gaps fast enough.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}